import { Box, Button, Group, LoadingOverlay, Modal, Text } from '@mantine/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import {
  useInvalidateVlmPipelines,
  useRestartVlmPipeline
} from '@/queries/vlmQueries'
import { showToast } from '@/theme/notifications'
import { Pipeline } from '@/types/vlm'

type VlmPipelineRestartModalProps = {
  opened: boolean
  pipeline: Pipeline | null
  onClose: () => void
}

export const VlmPipelineRestartModal = ({
  opened,
  pipeline,
  onClose
}: VlmPipelineRestartModalProps) => {
  const intl = useIntl()

  const { mutateAsync: restartPipeline, isPending } = useRestartVlmPipeline()
  const { invalidateVlmPipelineList } = useInvalidateVlmPipelines()

  const handleRestart = async () => {
    if (!pipeline) return

    try {
      await restartPipeline(pipeline.id)
      void invalidateVlmPipelineList()
      onClose()
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'vlm.pipelines.restart.error' })
        ),
        'error'
      )
    }
  }

  return (
    <Modal
      size="lg"
      title={<FormattedMessage id="vlm.pipelines.restart.title" />}
      opened={opened}
      onClose={onClose}
    >
      <Box pos="relative">
        <LoadingOverlay visible={isPending} />

        <Text size="sm">
          <FormattedMessage
            id="vlm.pipelines.restart.confirmation"
            values={{ name: <strong>{pipeline?.name}</strong> }}
          />
        </Text>

        <Group justify="end" mt="xl">
          <Button miw={120} variant="default" onClick={onClose}>
            <FormattedMessage id="cancel" />
          </Button>

          <Button miw={120} onClick={() => void handleRestart()}>
            <FormattedMessage id="vlm.pipelines.restart.confirm" />
          </Button>
        </Group>
      </Box>
    </Modal>
  )
}
